import { Link, createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { AngaMark } from "@/components/brand/AngaMark";
import { AngaWordmark } from "@/components/brand/AngaWordmark";
import { usePlatformConfig } from "@/lib/config";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy policy" },
      {
        name: "description",
        content:
          "Privacy policy for the flood and weather early-warning service, written for the Data Protection Act, 2019 of Kenya.",
      },
    ],
  }),
  component: PrivacyPolicyPage,
});

const EFFECTIVE = "12 October 2026";
const VERSION = "2026-10-12";

const TOC = [
  ["who", "1. Who this notice is for"],
  ["law", "2. The law that applies"],
  ["controller", "3. Data controller"],
  ["service", "4. What the service does"],
  ["data", "5. Personal data we collect"],
  ["mandatory", "6. Mandatory and voluntary data"],
  ["purposes", "7. Purposes and lawful bases"],
  ["consent", "8. Consent and withdrawal"],
  ["sensitive", "9. Sensitive data and children"],
  ["location", "10. Location"],
  ["alerts", "11. Alerts and messages"],
  ["reports", "12. Hazard reports and photos"],
  ["ai", "13. Automated assessment and AI"],
  ["sharing", "14. Who receives personal data"],
  ["transfers", "15. Transfers outside Kenya"],
  ["security", "16. Security"],
  ["retention", "17. How long we keep data"],
  ["rights", "18. Your rights and how to use them"],
  ["complaints", "19. Complaints to the ODPC"],
  ["changes", "20. Changes to this notice"],
] as const;

function PrivacyPolicyPage() {
  const { platformName } = usePlatformConfig();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-3xl px-6 py-10 md:px-8 md:py-14">
        <header className="flex items-center justify-between gap-4 border-b border-border/60 pb-6">
          <Link to="/" className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#064E3B] text-white">
              <AngaMark className="h-6 w-6" title={platformName} />
            </div>
            <div className="min-w-0">
              <AngaWordmark className="text-xl" />
              <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                Privacy policy
              </p>
            </div>
          </Link>
          <Link to="/" className="shrink-0 text-sm text-primary underline-offset-4 hover:underline">
            Back home
          </Link>
        </header>

        <article className="pt-8 text-sm leading-relaxed text-foreground/90">
          <h1 className="font-display text-3xl font-bold tracking-normal text-foreground md:text-4xl">
            Privacy policy
          </h1>
          <p className="mt-3 text-muted-foreground">
            Effective {EFFECTIVE}. Notice version {VERSION}. This is the privacy notice for{" "}
            <strong className="font-semibold text-foreground">{platformName}</strong>, a flood and
            heavy-rain early-warning service for people in Kenya.
          </p>
          <p className="mt-3">
            It is written to meet section 29 of the Data Protection Act, 2019 (the Act) and the
            Data Protection (General) Regulations, 2021. It explains what personal data is
            collected, why, who receives it, how it is protected, and the rights you have. Read it
            before you create an account. The short consent screen during sign-up is a summary.
            This page is the full notice.
          </p>

          <nav aria-label="Contents" className="mt-8 rounded-md border border-border bg-muted/40 p-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
              Contents
            </p>
            <ol className="mt-3 grid gap-1.5 sm:grid-cols-2">
              {TOC.map(([id, label]) => (
                <li key={id}>
                  <a href={`#${id}`} className="text-primary underline-offset-4 hover:underline">
                    {label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <Section id="who" title="1. Who this notice is for">
            <p>
              This notice covers residents and other people who visit the public site, create an
              account, save places, receive alerts, send a hazard report, or acknowledge an alert.
              It also covers limited data about administrators and operations staff who sign in to
              run the service.
            </p>
            <p>
              “Personal data” means information relating to an identified or identifiable natural
              person, as defined in section 2 of the Act. A phone number, email address, name,
              account identifier, and a precise location tied to your account are personal data.
              Weather observations about a county, with no link to a person, are not.
            </p>
          </Section>

          <Section id="law" title="2. The law that applies">
            <p>Processing is governed by Kenyan law, in particular:</p>
            <ul>
              <li>
                Article 31 of the Constitution of Kenya, 2010, which protects privacy, including
                the privacy of communications and of information relating to family or private
                affairs.
              </li>
              <li>
                The Data Protection Act, 2019, including the principles in section 25, the rights
                in section 26, the duty to notify in section 29, the lawful bases in section 30,
                consent in section 32, children in section 33, security in section 41, sensitive
                personal data in sections 44 and 45, and cross-border transfers in sections 48 and
                49.
              </li>
              <li>
                The Data Protection (General) Regulations, 2021, including the elements of a
                privacy notice and the time for responding to an access request.
              </li>
              <li>
                The Data Protection (Registration of Data Controllers and Data Processors)
                Regulations, 2021, where registration with the Office of the Data Protection
                Commissioner (ODPC) is required.
              </li>
              <li>
                The Data Protection (Complaints Handling and Enforcement Procedures) Regulations,
                2021, if you complain to the ODPC.
              </li>
              <li>
                The Kenya Information and Communications Act and its regulations, to the extent
                they apply to electronic messages, and the Computer Misuse and Cybercrimes Act,
                2018, as they relate to unauthorised access and the security of systems.
              </li>
            </ul>
            <p>
              If a rule in this notice conflicts with the Act or a regulation, the Act or
              regulation prevails.
            </p>
          </Section>

          <Section id="controller" title="3. Data controller">
            <p>
              The data controller is the organisation that operates {platformName}. The public name
              of the service is taken from the organisation settings and is shown in the product as{" "}
              <strong className="font-semibold text-foreground">{platformName}</strong>. The
              controller decides why and how personal data is processed. Message gateways, map
              providers, email hosts, and AI providers act as data processors or, where the law
              treats them as independent controllers for their own systems, as separate controllers
              of the data they need to perform their service.
            </p>
            <p>
              Where the Act requires registration, the controller keeps its ODPC registration
              status. This page does not display a certificate number because that status is held
              in the operator’s records and can change.
            </p>
            <p>To reach the controller about your personal data:</p>
            <ul>
              <li>
                Sign in and use the export or deletion request in account settings. That is the
                primary channel, because it is tied to your account and can be verified.
              </li>
              <li>
                If you cannot sign in, write from the email address or phone number registered on
                the account and state the right you want to use (access, correction, objection, or
                deletion). We use that contact to confirm the request is yours before we disclose
                or change data.
              </li>
            </ul>
            <p>
              We do not ask you to send copies of your national identity card for an ordinary
              privacy request. If we cannot be sure a request is genuine, we may ask a reasonable
              follow-up question that does not collect more identity data than necessary.
            </p>
          </Section>

          <Section id="service" title="4. What the service does">
            <p>
              {platformName} warns people about flood, heavy rain, and related hazards for places
              they care about, and for where they are while the app is open if they allow it.
              Hazard information is drawn from weather, flood, fire, and earthquake sources, and
              from reports people submit. Advisories are prepared for an area, reviewed under the
              operating rules of the service, and sent by the channels you choose.
            </p>
            <p>
              Messages from this service are platform advisories. They are not, by themselves,
              official warnings of the Kenya Meteorological Department or another public authority.
              Where an official warning is relayed, the message says so and names the source.
              Follow emergency services and official guidance if it differs from an advisory.
            </p>
          </Section>

          <Section id="data" title="5. Personal data we collect">
            <p>We collect only what the service needs. The categories are:</p>
            <h3>Account and identity</h3>
            <ul>
              <li>Name.</li>
              <li>Email address, and whether it has been verified.</li>
              <li>Mobile phone number, and whether it has been verified.</li>
              <li>
                Password, stored only as a hash. We never store the password you type in readable
                form. If you sign in with Google, we store the Google account identifier needed to
                recognise you, and we do not receive your Google password.
              </li>
              <li>Authentication method (password, Google, or phone).</li>
              <li>Preferred interface language and the language used for alert text.</li>
              <li>
                Optional role you pick so the product can emphasise relevant tools, such as
                resident, driver, rider, parent, farmer, fisher, business, community, traveller,
                fleet, or responder. This is a product preference, not an official status.
              </li>
              <li>County and ward you associate with the account, and invite or cohort labels if signup is limited to an invited group.</li>
              <li>Account status, onboarding progress, and last activity time.</li>
            </ul>
            <h3>Consent</h3>
            <ul>
              <li>
                Which permissions you gave or refused: storing saved places, sending alerts, live
                location while the app is open, anonymous usage notes, and AI-written summaries.
              </li>
              <li>The version of this notice you were shown, and the time of each consent event.</li>
            </ul>
            <h3>Places and location</h3>
            <ul>
              <li>
                Saved places: a label, county, ward, and, when you provide or the device supplies
                them, latitude, longitude, accuracy, source (device or manual), and the time they
                were captured. Coordinates may be stored in encrypted form.
              </li>
              <li>
                A coarse grid cell (an H3 index) used to match you to a hazard area without
                keeping a continuous trail.
              </li>
              <li>
                Live location while the app is open, only if you allow it: coordinates or grid
                cell, accuracy, county or ward when known, whether the point is outside the area
                we can match to a hazard, and an expiry time. These records are short-lived. See
                section 10.
              </li>
              <li>
                Up to three recent distinct places derived from location you have shared, so the
                service can remember areas you have recently been in. Older entries are dropped.
              </li>
            </ul>
            <h3>Alerts and delivery</h3>
            <ul>
              <li>
                Channels you enable (SMS, email, WhatsApp, push), quiet hours, and the lowest
                severity you want to hear about. Severe alerts are still delivered. See section 11.
              </li>
              <li>
                Delivery records: channel, status, a masked form of the recipient address, provider
                reference, timestamps, error codes, cost of the send, and whether you acknowledged
                the message.
              </li>
              <li>
                Optional feedback on whether flooding was happening, was not happening, or you were
                not sure. The feedback is tied to the alert, not published with your name.
              </li>
            </ul>
            <h3>Hazard reports</h3>
            <ul>
              <li>
                Description, category, place label, county, ward, coordinates, and an optional
                photo. Photos are stored so they are not publicly listed. Reports can be rejected,
                including where an image is not appropriate for an alert.
              </li>
            </ul>
            <h3>Technical and security data</h3>
            <ul>
              <li>
                Session records: a hash of the session token, expiry, and, when the server
                receives them, IP address and browser or app user agent. The raw session token is
                not stored.
              </li>
              <li>
                One-time codes for verification or password reset, stored as hashes until they
                expire or are used.
              </li>
              <li>
                On this device, a small local note of onboarding steps (event name and time only,
                with no name, phone, or email) and your live-location preference. These stay in
                the browser unless you clear site data.
              </li>
            </ul>
            <h3>Administrators</h3>
            <p>
              Staff accounts store name, work email, role, a password hash, an optional one-time
              password secret, last sign-in time, and an audit trail of sensitive actions (who did
              what, to which record, when, and from which IP). Staff data is used to secure the
              service and to show accountability for alert approvals and data requests.
            </p>
            <p>
              We do not ask for your national identity number, KRA PIN, biometric identifiers,
              health records, religion, ethnicity, or political opinions. Do not put those in a
              hazard report or a photo.
            </p>
          </Section>

          <Section id="mandatory" title="6. Mandatory and voluntary data">
            <p>
              Section 29 of the Act requires us to say what is required, what is optional, and what
              happens if you do not provide it.
            </p>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[32rem] border-collapse text-left text-xs">
                <thead>
                  <tr className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 pr-3 font-semibold">Data</th>
                    <th className="py-2 pr-3 font-semibold">Required?</th>
                    <th className="py-2 font-semibold">If you do not provide it</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Name, email, phone, and a password or Google sign-in", "Required for an account", "You cannot create an account or receive alerts tied to you."],
                    ["Verification of phone or email", "Required to trust the channel", "We will not send alerts to an unverified channel."],
                    ["Saved place (county, ward, or coordinates)", "Required for the service", "We cannot match flood risk to your area. This consent is required to continue."],
                    ["Permission to send alerts", "Required for the service", "The product has no way to warn you. This consent is required to continue."],
                    ["Live location while the app is open", "Optional", "Alerts and risk use your saved places only. You can still use the service."],
                    ["WhatsApp, push, or a second channel", "Optional", "We use the channels you leave on. SMS is the default channel."],
                    ["Usage notes and AI summaries", "Optional", "You still receive alerts. Summaries stay on the standard wording."],
                    ["Hazard report or photo", "Optional", "Nothing is submitted. Reporting is never a condition of receiving alerts."],
                    ["Invite code, when signup is invite-only", "Required in that mode", "The account is not created until a valid invite is supplied."],
                  ].map(([item, required, effect]) => (
                    <tr key={item} className="border-b border-border/70 align-top">
                      <td className="py-2 pr-3">{item}</td>
                      <td className="py-2 pr-3">{required}</td>
                      <td className="py-2">{effect}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p>
              Collection is not required by a statute that forces you to register for flood
              alerts. You choose to use the service. Once you use it, some processing is necessary
              to perform that service, to keep the system secure, and, for severe hazards, to
              protect vital interests. Those bases are set out in section 7.
            </p>
          </Section>

          <Section id="purposes" title="7. Purposes and lawful bases">
            <p>
              We process personal data only for specified purposes. Under section 30 of the Act and
              regulation 5 of the General Regulations, each processing operation rests on one
              lawful basis, chosen before the processing starts. We do not sell personal data and
              we do not use it for advertising or any other commercial purpose under section 37 of
              the Act.
            </p>
            <ul>
              <li>
                <strong className="font-semibold text-foreground">To provide the service you asked for.</strong>{" "}
                Creating the account, storing the places you save, matching hazards, and sending
                alerts on the channels you enable. Lawful basis: steps you ask us to take before
                you join, and then performance of the service contract (section 30(1)(b)(i)). These
                steps are shown on the sign-up screen so you know about them. They are not treated
                as consent, because regulation 4 says consent is not freely given if you cannot
                refuse it without losing the service.
              </li>
              <li>
                <strong className="font-semibold text-foreground">To protect life and safety in a severe hazard.</strong>{" "}
                Delivering a severe alert even during quiet hours. Lawful basis: vital interests of
                you or of another person (section 30(1)(b)(iii)).
              </li>
              <li>
                <strong className="font-semibold text-foreground">To keep the service secure.</strong>{" "}
                Sessions, abuse checks, and audit logs of staff actions. Lawful basis: legitimate
                interests in running a secure warning system, which do not override your rights
                (section 30(1)(b)(vi)).
              </li>
              <li>
                <strong className="font-semibold text-foreground">To keep a record the law requires.</strong>{" "}
                Proof of a consent choice, a privacy-request outcome, and a disclosure the ODPC or
                a court orders. Lawful basis: legal obligation (section 30(1)(b)(ii)), including
                the duty under section 32 to be able to show consent.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Optional processing.</strong>{" "}
                Live location while the app is open, anonymous onboarding notes, AI-written
                summaries, and any channel you are not required to use. Lawful basis: consent
                (section 32 and regulation 4). You may refuse each one separately.
              </li>
            </ul>
            <p>
              If we want to use personal data for a new purpose that is not compatible with the
              purposes above, we will tell you and, where the Act requires it, ask for fresh
              consent before we do so.
            </p>
          </Section>

          <Section id="consent" title="8. Consent and withdrawal">
            <p>
              Where we rely on consent, section 32 and regulation 4 require it to be express,
              specific, informed, and freely given. You must have capacity to give it. It must be
              voluntary, and as easy to withdraw as to give. The burden of showing consent is on
              the controller. Before asking, we tell you who the controller is, the purpose, the
              data involved, whether a third party will receive it, the risk of a transfer outside
              Kenya where safeguards are limited, and what happens if you say no.
            </p>
            <p>
              The sign-up screen separates each choice. Optional items stay off until you turn them
              on. Silence, a pre-ticked optional box, or merely browsing the public page is not
              consent. Items you cannot refuse without losing flood alerts are described on that
              screen, but their lawful basis is the service contract, not consent.
            </p>
            <p>You may withdraw optional consent at any time. We then stop that processing, as regulation 4(5) requires:</p>
            <ul>
              <li>Turn off live location. New live points stop, and existing ones expire.</li>
              <li>Turn off a channel (SMS, WhatsApp, email, or push) in settings.</li>
              <li>Turn off anonymous usage notes and AI summaries.</li>
              <li>
                Ask for deletion of the account if you want to stop the service itself. Stopping
                the service is different from withdrawing one optional permission. Places and alert
                delivery are necessary to the service you signed up for, so the way to end that
                processing is to close the account or to object as described in section 18.
              </li>
            </ul>
            <p>
              Withdrawal does not undo a message already sent. It stops new processing that
              depended on that consent. A delivery log that no longer needs your consent, because
              it is kept for security or to show what was sent, is kept only for the period in
              section 17.
            </p>
          </Section>

          <Section id="sensitive" title="9. Sensitive data and children">
            <p>
              “Sensitive personal data” under section 2 of the Act includes data revealing health,
              ethnic or social origin, conscience or belief, genetic or biometric data, property
              details, marital status, family details (including names of children, parents, or a
              spouse), sex, or sexual orientation. We do not request these categories.
            </p>
            <p>
              A precise home or workplace location can reveal where you live. We treat coordinates
              as high-risk personal data even where they are not “property details” in the sense of
              title or ownership records: access is limited, coordinates may be encrypted, and they
              are not published on public maps as your identity. A hazard area shown to other users
              is an area risk, not your saved place and not your name.
            </p>
            <p>
              Do not include images of people, children, identity documents, injuries, or home
              interiors in a hazard photo. Reports that are not usable as alerts, including images
              we should not circulate, are rejected and are not fanned out.
            </p>
            <p>
              Section 33 of the Act restricts processing of a child’s personal data. This service
              is for adults. You must be at least 18 years old to create an account. We do not
              knowingly register a child. A “parent” preference only changes which tools are
              emphasised; it is not a profile of your children and we do not collect their names.
              If you believe a child has an account, ask for deletion under section 18 and we will
              close it.
            </p>
          </Section>

          <Section id="location" title="10. Location">
            <p>Location is collected in three different ways. They are not the same thing.</p>
            <ul>
              <li>
                <strong className="font-semibold text-foreground">Saved places.</strong> You choose
                them (home, work, a farm, a route). They remain until you edit or delete them, or
                until the account is deleted. They are how we decide which area advisories apply
                to you.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Live location while the app is open.</strong>{" "}
                Optional. The browser or device is asked only after you allow it. On the web, it
                updates while this app is open. It is not a background tracker and it is not used
                when the app is closed. Each point carries an expiry and is removed on a short
                cycle measured in hours (about 24 hours), not kept as a travel history.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Recent places.</strong> At most
                three distinct areas recently seen from location you shared, so a return to a known
                area can be recognised. This is a cap, not a log of every point.
              </li>
            </ul>
            <p>
              If a point falls outside Kenya or cannot be matched to a hazard area, we store a flag
              that it is outside coverage and we do not pretend to send a local flood SMS for it.
              Map and forecast providers may receive a coordinate or place name in order to draw a
              map or fetch weather. They do not receive your name or phone number as part of that
              call.
            </p>
            <p>
              You can refuse device location in the browser, turn the in-app switch off, or delete
              a saved place. Device settings (Android, iOS, or the browser) are the final control
              for GPS. If the device blocks location, we keep using saved places only.
            </p>
          </Section>

          <Section id="alerts" title="11. Alerts and messages">
            <p>
              Alerts can be sent by SMS, email, WhatsApp, and push notification, according to the
              channels on your account. SMS is on by default because it reaches basic phones.
              WhatsApp is off until you enable it. You can change channels, quiet hours, language,
              and the lowest severity you want.
            </p>
            <p>
              Quiet hours suppress non-severe messages. A severe alert is still sent, because
              waiting until morning can defeat the point of a flood warning. That override is
              disclosed at sign-up and rests on vital interests as well as the service you
              requested.
            </p>
            <p>
              Delivery partners see the address they need in order to deliver (a phone number for
              SMS or WhatsApp, an email address for email, a push token for push). Our own logs
              keep a masked recipient value and a provider reference so we can trace failure
              without copying the full address into every operational screen.
            </p>
            <p>
              An acknowledgement link in a message records that the alert was seen, and may invite
              a short ground observation (flooding, no flooding, or not sure). That response is
              used to understand whether the warning matched conditions. It is not published with
              your identity.
            </p>
            <p>
              We do not send marketing. Service messages about your account (verification codes,
              password reset, a change to this notice) are part of operating the account.
            </p>
          </Section>

          <Section id="reports" title="12. Hazard reports and photos">
            <p>
              A signed-in person may report a local hazard, such as flooding or a blocked road.
              The report includes the text you write, the place, and any photo you attach. It is
              reviewed before it can be turned into an alert for other people. Review may include
              automated checks and a person on the operations side. A report can be rejected, for
              example if it is not an alert or if the image should not be circulated.
            </p>
            <p>
              If a report is approved, other people may receive an area advisory. They do not
              receive your name, phone number, or email. Do not write those into the description.
              Photos stay on access-controlled storage. They are not a public gallery.
            </p>
            <p>
              Submitting a report is optional. False or abusive reports may be refused and, where
              the law allows, may be retained long enough to protect other users and the integrity
              of the warning system.
            </p>
          </Section>

          <Section id="ai" title="13. Automated assessment and AI">
            <p>
              Hazard levels are calculated from weather and flood inputs, area rules, and, where
              relevant, reports. A proposal can be queued for a person to approve before messages
              go out. Some severe paths can send after a short, configured delay if they are not
              stopped, so that a fast-moving hazard is not stuck waiting. This is operational
              triage for public warnings. It is not a decision about credit, employment, insurance,
              or any legal right of yours.
            </p>
            <p>
              Separate from that, you may allow AI-written summaries. Those prompts are built from
              non-identifying hazard context (such as area, severity, and weather factors). We do
              not intentionally include your name, phone number, or email in those prompts. If you
              leave this option off, you still get the standard alert text.
            </p>
            <p>
              Report text may be checked by an AI provider to help decide whether it is a real
              hazard and to keep unsafe images out of the fan-out. That check is part of operating
              reports safely. Providers used for this are named in section 14. You can choose not
              to submit a report.
            </p>
            <p>
              You may ask for a human review of an alert decision that affected you, using the
              contact path in section 18. We can correct a wrong place, stop a channel, or explain
              the severity that was applied.
            </p>
          </Section>

          <Section id="sharing" title="14. Who receives personal data">
            <p>
              We share personal data with processors who perform a stated task, under instructions
              consistent with this notice, and only to the extent they need it. The main recipients
              are:
            </p>
            <ul>
              <li>
                <strong className="font-semibold text-foreground">SMS Leopard</strong> and, where
                used, <strong className="font-semibold text-foreground">Africa’s Talking</strong> —
                SMS and related messaging to Kenyan numbers. They receive the destination number
                and the message.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Email infrastructure</strong>{" "}
                (the operator’s SMTP host and, as a backup, Resend or a similar provider) — your
                email address and the message content.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Meta (WhatsApp Cloud API)</strong>{" "}
                — phone number and the approved template or alert text, only if you enable
                WhatsApp.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Google Firebase Cloud Messaging</strong>{" "}
                and, on Apple devices, <strong className="font-semibold text-foreground">Apple Push Notification service</strong>{" "}
                — a device push token and the alert payload, if you enable push.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Google</strong> — if you choose
                Google sign-in, Google authenticates you and returns the identifiers we need to
                open your account. Google’s own privacy notice applies to your use of Google’s
                service.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Mapbox</strong> and, if enabled,{" "}
                backup routing providers — coordinates or place queries to draw maps and routes.
                Not your phone number.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Anthropic (Claude)</strong> and{" "}
                <strong className="font-semibold text-foreground">DeepSeek</strong> — text needed
                for an optional summary or for report triage, as described in section 13. DeepSeek
                is used as a fallback and for report checks.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Cloud hosting and object storage</strong>{" "}
                (the database host, and S3-compatible storage such as AWS, Cloudflare R2, or a
                private MinIO instance) — account data and report photos in the regions the
                operator has contracted.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Operations staff</strong> of the
                controller, on a role basis, for approving alerts, handling privacy requests, and
                investigating incidents. Access is limited by role and is audited.
              </li>
            </ul>
            <p>
              Public social posts (for example an organisational advisory on a public network) do
              not include your name, phone number, email, or the precise coordinates of a saved
              place.
            </p>
            <p>
              Weather, satellite, and disaster feeds (such as Open-Meteo, national meteorological
              products, NASA fire data, and earthquake feeds) are sources of hazard information. We
              do not send them your identity. A forecast request may include a coordinate for the
              area being assessed.
            </p>
            <p>
              We may disclose personal data if the law requires it, or to protect a person from a
              serious threat, and we limit that disclosure to what the request or the threat
              requires. A time-limited export of who was warned about a specific alert may be
              produced for an emergency review. It is not a standing database shared with third
              parties.
            </p>
          </Section>

          <Section id="transfers" title="15. Transfers outside Kenya">
            <p>
              Some processors in section 14 store or access data outside Kenya, including in the
              United States and, for DeepSeek, in China. Sections 48 and 49 of the Act allow a
              cross-border transfer only where there is proof of appropriate safeguards, including
              safeguards in the destination country, or where one of the conditions in section 48
              applies. Those conditions include your consent, a transfer necessary for the
              performance of a contract with you, and a transfer necessary for the public interest
              or for the vital interests of a person.
            </p>
            <p>For this service the safeguards and conditions are:</p>
            <ul>
              <li>
                Transfers that deliver the service you asked for (sign-in, maps, email, push,
                messaging) are necessary to perform the contract.
              </li>
              <li>
                Optional AI summaries are sent only with your consent, and you can refuse them
                without losing alerts.
              </li>
              <li>
                Processors are engaged under contracts that require confidentiality, use only on
                instruction, and security appropriate to the data.
              </li>
              <li>
                Where a destination does not offer protection comparable to the Act, we do not rely
                on “adequacy” alone. We rely on the contract, on minimisation (no name or phone in
                AI prompts), and on the specific condition in section 48 that applies to that flow.
              </li>
            </ul>
            <p>
              You may object to an optional transfer by turning off the feature that causes it
              (AI summaries, WhatsApp, push, or Google sign-in). Core hosting and SMS delivery
              cannot be split from the service. If you object to those, the remedy is not to use
              the account, or to ask for deletion.
            </p>
          </Section>

          <Section id="security" title="16. Security">
            <p>
              Section 41 of the Act requires appropriate technical and organisational measures.
              Measures in use include:
            </p>
            <ul>
              <li>Access over encrypted connections (HTTPS) in deployed environments.</li>
              <li>Passwords and one-time codes stored as hashes, not as the values you enter.</li>
              <li>Session tokens stored as hashes, with expiry and the ability to revoke a session.</li>
              <li>
                Field encryption (AES-GCM) for phone numbers, email addresses, and coordinates when
                that control is enabled for the deployment, plus blind indexes so a record can be
                found without keeping every lookup in clear text.
              </li>
              <li>Role-based access for staff, and audit logs of sensitive administrative actions.</li>
              <li>Masked recipient values in delivery operations.</li>
              <li>Report images stored outside the public web root and served only with authorisation.</li>
              <li>Separation of sample and test sends from live operational records.</li>
            </ul>
            <p>
              No online service can promise that a breach is impossible. If a breach creates a real
              risk to your rights, the controller will notify the ODPC and, where the Act requires
              it, notify you, within the times the Act and the ODPC require. Tell us if you believe
              your account has been accessed without permission, and change your password.
            </p>
          </Section>

          <Section id="retention" title="17. How long we keep data">
            <p>
              Section 25 requires that personal data be kept only as long as necessary for the
              purpose. Default periods for this service:
            </p>
            <ul>
              <li>
                <strong className="font-semibold text-foreground">Account, saved places, consent history, and preferences</strong>{" "}
                — for the life of the account, then deleted or irreversibly de-identified after a
                deletion request is completed, except for the narrow records below.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Live location</strong> — about 24
                hours from capture, then deleted by expiry.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Recent distinct places</strong> —
                at most three; older ones are overwritten.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Operational and delivery records</strong>{" "}
                — up to 365 days, unless a shorter or longer period is set in organisation settings
                because of an active investigation, a legal hold, or a need to prove that a warning
                was or was not sent. The default is 365 days.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Consent proof</strong> — the fact,
                time, and version of consent may be kept after other profile fields are removed, in
                minimised form, because section 32 places the burden of proof on the controller.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Privacy requests</strong> — kept
                long enough to show that the request was handled, including the type, status, and
                completion time.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Security and staff audit logs</strong>{" "}
                — kept for the period needed to investigate abuse and to account for administrative
                actions, then deleted or reduced.
              </li>
              <li>
                <strong className="font-semibold text-foreground">One-time codes</strong> — until
                they are used or they expire, whichever is sooner.
              </li>
            </ul>
            <p>
              Backups rotate on the host’s backup cycle. A deleted account may persist in an
              encrypted backup until that cycle overwrites it, and is not restored except for
              disaster recovery.
            </p>
          </Section>

          <Section id="rights" title="18. Your rights and how to use them">
            <p>Under section 26 of the Act you have the right to:</p>
            <ul>
              <li>Be informed of the use to which your personal data is put (this notice).</li>
              <li>Access your personal data in the controller’s possession.</li>
              <li>Object to processing of all or part of your personal data.</li>
              <li>Correction of false or misleading data.</li>
              <li>Deletion of false or misleading data.</li>
            </ul>
            <p>
              The Act and the General Regulations add further rights. The times below are the
              periods in those Regulations. A request may use the ODPC forms (Form DPG 1 to Form
              DPG 5) or the same request in writing through your account. An in-app export or
              deletion is the electronic form of that request.
            </p>
            <ul>
              <li>
                <strong className="font-semibold text-foreground">Access (regulation 9).</strong>{" "}
                Confirmation of whether we process your data, a copy of it, the purposes, the
                categories, the recipients (including those outside Kenya), and how long we keep
                it. We comply within seven days. If you ask electronically, we provide the copy in
                a common electronic form. There is no fee.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Correction (section 40 and regulation 10).</strong>{" "}
                Rectification of data that is untrue, inaccurate, outdated, incomplete, or
                misleading. We correct it within fourteen days when a correction is needed. If we
                refuse, we write to you within seven days with the reasons. There is no fee.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Objection (section 36 and regulation 8).</strong>{" "}
                We comply within fourteen days, at no fee. Objection to direct marketing is
                absolute. We do not do direct marketing. Where the basis is a compelling legitimate
                interest or a legal claim, we may decline, and we will explain why and tell you
                that you can complain to the ODPC.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Erasure (section 40 and regulation 12).</strong>{" "}
                You may ask us to erase data when it is no longer needed, when you withdraw the
                consent it relied on, when you object and there is no overriding legitimate
                interest, or when the processing is unlawful. We respond within fourteen days, at
                no fee. Erasure does not apply where we must keep the data to obey a legal
                obligation or to establish, exercise, or defend a legal claim. We then keep only
                the minimum, for the period in section 17.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Restriction (section 34 and regulation 7).</strong>{" "}
                You may ask us to restrict processing while accuracy is contested, where processing
                was unlawful and you prefer restriction to erasure, or while an objection is being
                considered. We respond within fourteen days, at no fee, and we tell any recipient
                who still holds the data. We may decline a request that is manifestly unfounded or
                excessive, and we will explain a refusal within fourteen days.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Portability (section 38 and regulation 11).</strong>{" "}
                A copy of personal data you provided, in a form you can take elsewhere. The
                Regulations allow up to thirty days and a fee that does not exceed the cost of
                fulfilling the request. The export in your account is provided at no charge.
              </li>
            </ul>
            <p>
              An export contains the personal data linked to you (profile, places, consent, and
              related records). It does not include other people’s data or the security secrets of
              the platform.
            </p>
            <p>
              <strong className="font-semibold text-foreground">How to ask.</strong> Sign in and
              submit an export or deletion request in settings, or write from the email or phone on
              the account as described in section 3. Say which right you are using. For a
              correction, say what is wrong and what the correct value is. Someone you have
              authorised may ask for you (section 27 and regulation 13). We may wait until the
              authority is clear.
            </p>
            <p>
              <strong className="font-semibold text-foreground">Verification and limits.</strong> We
              may refuse or narrow a request where the Act allows it: where we cannot verify that
              it is you, where disclosure would reveal another person’s personal data, or where a
              legal hold applies. Where we can erase only part of a record, we erase that part and
              explain what remains.
            </p>
            <p>
              Objection to optional processing is implemented by turning the feature off.
              Objection to processing that is necessary for the service is handled by closing the
              account, except where we must retain a minimised record on a basis in section 7.
            </p>
          </Section>

          <Section id="complaints" title="19. Complaints to the ODPC">
            <p>
              You may complain to the controller first, using section 3, and we will try to resolve
              it. You also have the right to complain directly to the Office of the Data Protection
              Commissioner, the independent office established under the Act.
            </p>
            <ul>
              <li>
                Website:{" "}
                <a
                  href="https://www.odpc.go.ke"
                  className="text-primary underline-offset-4 hover:underline"
                  rel="noreferrer"
                >
                  https://www.odpc.go.ke
                </a>
              </li>
              <li>
                Complaints can be lodged through the ODPC’s published channels (the office accepts
                complaints under section 56 of the Act and the Complaints Handling Regulations).
              </li>
            </ul>
            <p>
              The ODPC can investigate, order a controller to comply, and impose administrative
              fines. Nothing in this notice limits that right. You may also seek a remedy in the
              courts of Kenya.
            </p>
          </Section>

          <Section id="changes" title="20. Changes to this notice">
            <p>
              If we change what we collect or why, we will update this page, change the version
              date, and, where the change requires new consent, ask you before we rely on it.
              Continued use after a non-essential wording change does not count as consent to a
              new purpose. The version you accepted is stored with your consent record.
            </p>
            <p>
              This notice is the operational privacy policy of the service. It is written to be
              followed. It is not a certificate of ODPC registration and it does not replace advice
              from the ODPC on a specific complaint.
            </p>
            <p className="text-muted-foreground">
              Version {VERSION}. Effective {EFFECTIVE}.
            </p>
          </Section>
        </article>

        <footer className="mt-12 border-t border-border/60 pt-6 text-xs text-muted-foreground">
          <p>
            Advisories are not official government warnings. Follow emergency services if guidance
            differs.
          </p>
          <p className="mt-3">
            <Link to="/" className="text-primary underline-offset-4 hover:underline">
              Home
            </Link>
          </p>
        </footer>
      </div>
    </div>
  );
}

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="mt-10 scroll-mt-6">
      <h2 className="font-display text-xl font-bold text-foreground">{title}</h2>
      <div className="mt-3 space-y-3 [&_h3]:mt-4 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-foreground [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5">
        {children}
      </div>
    </section>
  );
}
